import { getWalletClient, getAccount } from '@wagmi/core';
import { IdentifierKind } from '@xmtp/browser-sdk';
import { toBytes } from 'viem';
import type { HorusSigner } from './types';

async function getWalletClientWithRetry(): Promise<any> {
  let walletClient = await getWalletClient();
  if (walletClient) return walletClient;

  // If wagmi shows an address (connected/reconnecting), wait for the connector
  // to finish initializing. WalletConnect needs to re-establish a WebSocket
  // session which can take several seconds after page load.
  const account = getAccount();
  if (account.address && !account.isDisconnected) {
    for (let i = 0; i < 10; i++) {
      await new Promise(r => setTimeout(r, 1000));
      walletClient = await getWalletClient();
      if (walletClient) return walletClient;
    }
  }

  throw new Error('No wallet client available. Please reconnect your wallet.');
}

export function createNonCustodialSigner(address: string): HorusSigner {
  const normalizedAddress = address.toLowerCase();

  return {
    type: 'EOA',
    getIdentifier: () => ({
      identifier: normalizedAddress,
      identifierKind: IdentifierKind.Ethereum,
    }),
    signMessage: async (message: string): Promise<Uint8Array> => {
      const walletClient = await getWalletClientWithRetry();
      const signature = await walletClient.signMessage({
        account: address as `0x${string}`,
        message,
      });
      return toBytes(signature);
    },
    signMessageHex: async (message: string): Promise<string> => {
      const walletClient = await getWalletClientWithRetry();
      return walletClient.signMessage({
        account: address as `0x${string}`,
        message,
      });
    },
  };
}
